<template>
  <div class="ec-page">

    <!-- ── Page Header ───────────────────────────────────────────────────── -->
    <div class="ec-header">
      <div class="ec-header__left">
        <div class="ec-header__icon">
          <v-icon size="22">mdi-account-group-outline</v-icon>
        </div>
        <div>
          <h1 class="ec-header__title">Users</h1>
          <p class="ec-header__subtitle">Manage user accounts, licences and permissions</p>
        </div>
      </div>
    </div>

    <edit-user :dialog="dialog" :user="getUser" v-on:saved="initialize" />
    <user-register :show="showRegisterDialog" @close="showRegisterDialog = false" @call-users="initialize" />

    <!-- Bulk delete confirm dialog -->
    <v-dialog :value="bulkDeleteDialog.show" max-width="420" persistent @input="bulkDeleteDialog.show = $event">
      <div class="bd-dialog">
        <div class="bd-dialog__header">
          <div class="bd-dialog__icon">
            <v-icon size="20" color="white">mdi-delete-outline</v-icon>
          </div>
          <h2 class="bd-dialog__title">Delete Selected Users?</h2>
        </div>
        <div class="bd-dialog__body">
          <p class="bd-dialog__message">
            <strong>{{ selectedUsers.length }}</strong> user{{ selectedUsers.length > 1 ? 's' : '' }} will be deleted.
          </p>
          <p class="bd-dialog__sub">This action cannot be undone.</p>
          <label class="bd-hard-option" :class="{ 'bd-hard-option--on': bulkDeleteDialog.hardDelete }">
            <input type="checkbox" v-model="bulkDeleteDialog.hardDelete" class="bd-hard-checkbox" />
            <span class="bd-hard-text">
              <strong>Permanently delete</strong>
              <span>Removes the user and all related data from the database. Cannot be restored.</span>
            </span>
          </label>
        </div>
        <div class="bd-dialog__footer">
          <button class="ec-btn ec-btn--ghost" :disabled="bulkDeleteDialog.loading" @click="bulkDeleteDialog.show = false">Cancel</button>
          <button class="ec-btn ec-btn--danger" :disabled="bulkDeleteDialog.loading" @click="confirmBulkDelete">
            <v-progress-circular v-if="bulkDeleteDialog.loading" size="13" width="2" indeterminate style="margin-right:6px" />
            {{ bulkDeleteDialog.hardDelete ? 'Permanently Delete' : 'Delete' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- ── Data table ────────────────────────────────────────────────────── -->
    <div class="ec-list">
      <admin-table-subheader
        v-model="search"
        search
        search-placeholder="Search by name, email or ID…"
        :refresh-button="true"
        :refresh-loading="usersLoading"
        :primary-button="{ label: 'New User', icon: 'mdi-plus', click: openRegisterDialog }"
        :secondary-buttons="[{ label: 'PDF', icon: 'mdi-file-pdf-box', click: exportData }]"
        @refresh="initialize"
      >
        <template v-slot:actions>
          <download-excel :data="users" :fields="json_fields" worksheet="Users" name="users.xls">
            <button class="ec-btn ec-btn--ghost" style="padding:6px 12px;font-size:.8rem">
              <v-icon size="14" style="margin-right:5px">mdi-file-excel-outline</v-icon>Excel
            </button>
          </download-excel>
        </template>
      </admin-table-subheader>

      <!-- Bulk action bar -->
      <transition name="bulk-bar">
        <div v-if="selectedUsers.length > 0" class="bulk-action-bar">
          <span class="bulk-action-bar__count">
            <v-icon size="15" style="margin-right:5px">mdi-checkbox-multiple-marked-outline</v-icon>
            {{ selectedUsers.length }} user{{ selectedUsers.length > 1 ? 's' : '' }} selected
          </span>
          <div style="display:flex;gap:8px;align-items:center">
            <button class="ec-btn ec-btn--ghost" style="padding:5px 12px;font-size:.8rem" @click="selectedUsers = []">
              Clear
            </button>
            <button class="ec-btn ec-btn--danger" style="padding:5px 12px;font-size:.8rem" @click="bulkDeleteDialog.show = true">
              <v-icon size="13" style="margin-right:4px">mdi-delete-outline</v-icon>
              Delete Selected
            </button>
          </div>
        </div>
      </transition>

      <v-data-table
        id="users-table"
        dense
        show-select
        item-key="id"
        v-model="selectedUsers"
        :headers="headers"
        :items="users"
        :server-items-length="totalUsers"
        :options.sync="tableOptions"
        :loading="usersLoading"
        loading-text="Loading users…"
        :footer-props="{ 'items-per-page-options': [10, 30, 50, -1] }"
        class="ec-table"
      >
        <template v-slot:[`item.is_active`]="{ item }">
          <span class="u-status-pill" :class="item.is_active ? 'u-pill--green' : 'u-pill--red'">
            <v-icon size="11" style="margin-right:3px">{{ item.is_active ? 'mdi-check' : 'mdi-close' }}</v-icon>
            {{ item.is_active ? 'Active' : 'Inactive' }}
          </span>
        </template>

        <template v-slot:[`item.deleted`]="{ item }">
          <span v-if="item.deleted" class="u-status-pill u-pill--red">
            <v-icon size="11" style="margin-right:3px">mdi-delete-outline</v-icon>Deleted
          </span>
          <span v-else class="u-status-pill u-pill--gray">—</span>
          <div v-if="item.deleted && item.deleted_date" class="u-meta-date">{{ timeParserDate(item.deleted_date) }}</div>
        </template>

        <template v-slot:[`item.email_confirmed_at`]="{ item }">
          <span v-if="item.email_confirmed_at" class="u-date">{{ timeParserDate(item.email_confirmed_at) }}</span>
          <span v-else class="u-status-pill u-pill--amber">Unconfirmed</span>
        </template>

        <template v-slot:[`item.last_login`]="{ item }">
          <span v-if="item.last_login" class="u-date">{{ timeParserDate(item.last_login) }}</span>
          <span v-else class="u-status-pill u-pill--gray">Never</span>
        </template>

        <template v-slot:[`item.blocked`]="{ item }">
          <span v-if="item.blocked" class="u-status-pill u-pill--red">
            <v-icon size="11" style="margin-right:3px">mdi-block-helper</v-icon>Blocked
          </span>
          <span v-else class="u-status-pill u-pill--gray">—</span>
          <div v-if="item.blocked && item.blocked_date" class="u-meta-date">{{ timeParserDate(item.blocked_date) }}</div>
        </template>

        <template v-slot:[`item.dev`]="{ item }">
          <span v-if="item.dev" class="u-status-pill u-pill--purple">Dev</span>
          <span v-else class="u-status-pill u-pill--gray">—</span>
        </template>

        <template v-slot:[`item.licences`]="{ item }">
          <button class="u-inline-btn" @click="showUserLicencesTable(item)">
            <v-icon size="13" style="margin-right:4px">mdi-card-account-details-outline</v-icon>
            Licences
          </button>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <user-actions-menu @call-users="initialize" :userSource="item" />
        </template>
      </v-data-table>
    </div>

    <user-licences-card
      :userLicencesDialog="showUserLicencesDialog"
      :userLicences="userLicences"
      :currentUser="currentUser"
      @close-userLicence-dialog="showUserLicencesDialog = false"
      @sync-user-licences="getUserLicences"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import editUser from '../components/users/UserEdit.vue'
import { apiService } from '../service/api.service'
import find from 'lodash/find'
import converter from '@/mixins/converter'
import UserLicencesCard from '../components/users/UserLicencesCard.vue'
import UserActionsMenu from '../components/users/UserActionsMenu.vue'
import UserRegister from '../components/users/UserRegister.vue'
import AdminTableSubheader from '../components/shared/AdminTableSubheader.vue'
import JsPdf from 'jspdf'
import autoTable from 'jspdf-autotable'
import JsonExcel from 'vue-json-excel'
Vue.component('downloadExcel', JsonExcel)

export default Vue.extend({
  name: 'Users',
  mixins: [converter],
  data () {
    return {
      editDialog: false,
      usersLoading: false,
      dialogRegister: false,
      showRegisterDialog: false,
      selectedUsers: [],
      bulkDeleteDialog: { show: false, loading: false, hardDelete: false },
      showUserLicencesDialog: false,
      currentUser: {},
      color: 'red',
      search: null,
      totalUsers: 0,
      tableOptions: {
        page: 1,
        itemsPerPage: 30,
        sortBy: ['id'],
        sortDesc: [true]
      },
      userLicences: [],
      users: [],
      licences: [],
      licencesFeatures: [],
      getUser: {},
      dialog: { condition: false, register: false },
      json_fields: {
        Name: 'name',
        Email: 'email',
        Active: 'is_active',
        Deleted: 'deleted',
        'Email Confirmed Time': 'email_confirmed_at',
        Blocked: 'blocked',
        'Last Login': 'last_login'
      },
      headers: [
        { text: 'ID', value: 'id', width: 70 },
        { text: 'Name', value: 'name', width: 160 },
        { text: 'Email', value: 'email', width: 220 },
        { text: 'Active', value: 'is_active', width: 110 },
        { text: 'Deleted', value: 'deleted', width: 110 },
        { text: 'Email Confirmed', value: 'email_confirmed_at', width: 155 },
        { text: 'Blocked', value: 'blocked', width: 110 },
        { text: 'Last Login', value: 'last_login', width: 150 },
        { text: 'Licences', value: 'licences', align: 'center', width: 110 },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: 80 }
      ]
    }
  },
  mounted () { this.initialize() },
  watch: {
    tableOptions: {
      handler () { this.initialize() },
      deep: true
    },
    search () {
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        if (this.tableOptions.page !== 1) {
          this.tableOptions.page = 1
        } else {
          this.initialize()
        }
      }, 400)
    }
  },
  components: { editUser, UserLicencesCard, UserActionsMenu, UserRegister, AdminTableSubheader },
  methods: {
    openRegisterDialog () { this.showRegisterDialog = true },
    exportData () {
      const doc = new JsPdf()
      autoTable(doc, {
        head: [['No', 'Name', 'Email', 'ID', 'Active', 'Deleted', 'Email Confirmed', 'Blocked', 'Last Login']],
        body: this.users.map((u, i) => [i + 1, u.name, u.email, u.id, u.is_active, u.deleted, u.email_confirmed_at, u.blocked, u.last_login])
      })
      doc.save('users-table.pdf')
    },
    async initialize () {
      this.usersLoading = true
      const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions
      const params = {
        page,
        per_page: itemsPerPage,
        sort_by: sortBy[0] || 'id',
        sort_desc: sortDesc[0] !== false,
        search: this.search || ''
      }
      await apiService.get('admin-get-users', { params })
        .then((response) => {
          this.users = response.data.data
          this.totalUsers = response.data.total
        })
        .finally(() => (this.usersLoading = false))
    },
    showUserLicencesTable (user) {
      this.showUserLicencesDialog = true
      this.currentUser = user
      this.getUserLicences(user.id)
    },
    async getUserLicences (userId) {
      await apiService.get(`admin-user-licences/${userId}`)
        .then((res) => { this.userLicences = res.data.data })
        .catch((err) => { console.log(err.response) })
    },
    editUser (userId) {
      this.dialog.condition = true
      this.getUser = find(this.users, { id: userId })
    },
    async confirmBulkDelete () {
      this.bulkDeleteDialog.loading = true
      try {
        const ids = this.selectedUsers.map(u => u.id)
        await apiService.put('admin-bulk-delete-users', {
          user_ids: ids,
          hard_delete: this.bulkDeleteDialog.hardDelete
        })
        this.selectedUsers = []
        this.bulkDeleteDialog.show = false
        this.bulkDeleteDialog.hardDelete = false
        await this.initialize()
      } catch (e) {
        console.error(e)
      } finally {
        this.bulkDeleteDialog.loading = false
      }
    },
    checkCondition (user, condition) {
      if (!condition) return true
      let finalDecision = true
      for (let i = 0; i < Object.keys(condition).length; i++) {
        const cond = Object.keys(condition)[i]
        if (user[cond] !== condition[cond]) { finalDecision = false; break }
      }
      return finalDecision
    }
  }
})
</script>

<style scoped>
.bulk-action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: var(--ec-primary-bg);
  border-bottom: 1px solid var(--ec-primary);
}
.bulk-action-bar__count {
  display: flex;
  align-items: center;
  font-size: .82rem;
  font-weight: 600;
  color: var(--ec-primary);
}
.bulk-bar-enter-active, .bulk-bar-leave-active { transition: all .15s ease; }
.bulk-bar-enter, .bulk-bar-leave-to { opacity: 0; transform: translateY(-4px); }

/* ── Bulk delete dialog ─────────────────────────────── */
.bd-dialog {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
  box-shadow: 0 20px 60px rgba(0,0,0,.18);
}
.bd-dialog__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
}
.bd-dialog__icon {
  width: 34px; height: 34px;
  border-radius: 50%;
  background: #ef4444;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.bd-dialog__title {
  font-size: .95rem;
  font-weight: 700;
  color: #ef4444;
  margin: 0;
}
.bd-dialog__body {
  padding: 20px 20px 8px;
}
.bd-dialog__message {
  font-size: .88rem;
  color: #42526e;
  margin: 0 0 4px;
  line-height: 1.5;
}
.bd-dialog__sub {
  font-size: .78rem;
  color: #97a0af;
  margin: 0 0 16px;
}
.bd-dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #f1f2f4;
}

/* Hard-delete option row */
.bd-hard-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  cursor: pointer;
  transition: border-color .15s, background .15s;
  margin-bottom: 16px;
}
.bd-hard-option--on {
  border-color: #ef4444;
  background: #fef2f2;
}
.bd-hard-checkbox {
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: #ef4444;
  width: 15px; height: 15px;
  cursor: pointer;
}
.bd-hard-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.bd-hard-text strong {
  font-size: .84rem;
  color: #172b4d;
}
.bd-hard-text span {
  font-size: .76rem;
  color: #6b778c;
  line-height: 1.4;
}
</style>
